import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: BASE });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('agent_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r.data,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('agent_token');
      localStorage.removeItem('agent_user');
      window.location.reload();
    }
    return Promise.reject(err.response?.data || err);
  }
);

export const authAPI = {
  login: (u, p) => api.post('/auth/login', { username: u, password: p }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout')
};

export const agentAPI = {
  setStatus: (id, status, pauseCode, campaignId, note) => api.post(`/agents/${id}/status`, { status, pause_code_id: pauseCode, campaign_id: campaignId, pause_note: note || null }),
  loginCampaign: (id, campaignId) => api.post(`/agents/${id}/login-campaign`, { campaign_id: campaignId }),
  audioCheck:   (id) => api.post(`/agents/${id}/audio-check`),
  getAssignedCampaigns: (id) => api.get(`/agents/${id}/assigned-campaigns`)
};

export const sipAPI = {
  getConfig: () => api.get('/sip/config')
};

// Agents list (for team member selection)
export const agentsAPI = {
  list: () => api.get('/agents'),
};

export const campaignsAPI = {
  list: () => api.get('/campaigns')
};

export const leadsAPI = {
  get: id => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  // Look up leads by phone (account-scoped on the backend) — used to show the
  // lead sheet when an agent manually dials a number that's in the lead data.
  search: phone => api.get('/leads', { params: { search: phone, limit: 5 } }),
  // History search (Karim 2026-10-22): free-text (phone / name / email) over
  // ALL campaign leads — the backend scopes by account.
  searchAll: q => api.get('/leads', { params: { search: q, limit: 50 } })
};

export const callsAPI = {
  // Active (ringing/answered) calls for this account — used to recover the lead
  // for a call whose 'call:assigned' socket event was missed during a reconnect.
  active: () => api.get('/calls/active'),
  // My undispositioned answered call whose customer hung up = I'm in wrap-up.
  // Powers the reload-restore: re-enter wrap-up instead of going available.
  pendingDispo: () => api.get('/calls/pending-dispo'),
  // Log an agent's manual dial as a call record so it can be dispositioned.
  manual: (data) => api.post('/calls/manual', data),
  // Call history (2026-10-22): filters agent_id/campaign_id/status/dates.
  // The backend scopes by ACCOUNT (campaignClause) — the agent UI passes its
  // OWN user id; every row carries full name + phone + disposition label.
  list: (params) => api.get('/calls', { params }),
  disposition: (id, data) => api.post(`/calls/${id}/disposition`, data),
  hangup: id => api.post(`/calls/${id}/hangup`),
  hold: (id, hold) => api.post(`/calls/${id}/hold`, { hold }),
  transfer: (id, ext) => api.post(`/calls/${id}/transfer`, { extension: ext }),
  park: id => api.post(`/calls/${id}/park`)
};

export const adminAPI = {
  pauseCodes: () => api.get('/admin/pause-codes'),
  dispositions: (campaignId) => api.get('/admin/dispositions', { params: campaignId ? { campaign_id: campaignId } : {} })
};

// Team management (supervisor only)
export const teamAPI = {
  list: () => api.get('/agents/teams'),
  create: data => api.post('/agents/teams', data),
  get: id => api.get(`/agents/teams/${id}`),
  update: (id, data) => api.put(`/agents/teams/${id}`, data),
  delete: id => api.delete(`/agents/teams/${id}`),
  addAgent: (teamId, agentId) => api.post(`/agents/teams/${teamId}/agents`, { agent_id: agentId }),
  removeAgent: (teamId, agentId) => api.delete(`/agents/teams/${teamId}/agents/${agentId}`),
  getSettings: teamId => api.get(`/agents/teams/${teamId}/settings`),
  updateSettings: (teamId, data) => api.put(`/agents/teams/${teamId}/settings`, data),
  getQueue: teamId => api.get(`/agents/teams/${teamId}/queue`),
  assignQueue: (teamId, queueId, agentId) => api.post(`/agents/teams/${teamId}/queue/${queueId}/assign`, { agent_id: agentId }),
  completeQueue: (teamId, queueId) => api.post(`/agents/teams/${teamId}/queue/${queueId}/complete`),
  autoAssignQueue: teamId => api.post(`/agents/teams/${teamId}/queue/auto-assign`),
};

export const callbacksAPI = {
  list:      (params) => api.get('/callbacks', { params }),
  count:     () => api.get('/callbacks/count'),
  setStatus: (id, status) => api.put(`/callbacks/${id}/status`, { status })
};

export const bookedAPI = {
  // The agent's own booked leads (sales/appointments) for a period.
  mine: (period) => api.get('/appointments/mine', { params: { period } }),
  // Cancel one of MY booked calls (mistaken validation) — audit-noted.
  setStatus: (id, status, note) => api.post(`/appointments/${id}/status`, { status, note })
};

export const rdvAPI = {
  // Rendez-vous grid — single source of truth for appointments.
  list:   (params) => api.get('/rdv', { params }),
  counts: () => api.get('/rdv/counts'),
  // Réservation ATOMIQUE depuis le script (2026-10-16) : RDV + fiche client en
  // une seule transaction serveur, champs obligatoires validés côté serveur.
  // Idempotent — le bouton « Réessayer » ne crée jamais de doublon.
  book:   (data) => api.post('/rdv/book', data),
  create: (data) => api.post('/rdv', data),
  update: (id, data) => api.put(`/rdv/${id}`, data),
  remove: (id) => api.delete(`/rdv/${id}`),
  retour: (id, data) => api.put(`/rdv/${id}/retour`, data),
  cancelError: (id) => api.post(`/rdv/${id}/cancel-error`),
  dnclCheck: (phone) => api.get('/rdv/dncl-check', { params: { phone } }),
  // No-show / refus list (Karim 2026-10-29) : RDVs dont le client ne s'est pas
  // présenté ou a refusé — agents les rappellent pour replanifier.
  noShow: () => api.get('/rdv/no-show'),
};

export const advisorsAPI = {
  // Active advisors for the calling-script flow (RDV step + resource links).
  list: () => api.get('/advisors')
};

export default api;
